import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { KeyRound, ShieldCheck } from "lucide-react";
import { useAuth, useAuthedAxios } from "@/contexts/AuthContext";
import { FAQ_IMAGE } from "@/data/content";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AtelierChangePassword() {
  const { status, admin, clearMustChangePassword } = useAuth();
  const api = useAuthedAxios();
  const navigate = useNavigate();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (status === "anonymous") return <Navigate to="/atelier/login" replace />;

  const mustChange = !!admin?.must_change_password;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 12) return setError("New password must be at least 12 characters.");
    if (next !== confirm) return setError("New password and confirmation do not match.");
    if (next === current) return setError("New password must differ from the current password.");
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      clearMustChangePassword();
      navigate("/atelier", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="atelier-change-password" className="relative min-h-screen bg-obsidian flex items-center justify-center overflow-hidden">
      <img src={FAQ_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian/85 via-obsidian/90 to-obsidian" />
      <div
        className="absolute inset-0 pointer-events-none opacity-40 mix-blend-overlay"
        style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(183,166,200,0.35), transparent 60%)" }}
      />

      <div className="relative z-10 w-full max-w-md px-6 md:px-0">
        {mustChange && (
          <div className="mb-6 border border-lavender/50 bg-lavender/[0.06] px-5 py-4 flex items-start gap-3">
            <ShieldCheck size={18} className="text-lavender mt-0.5 shrink-0" />
            <div>
              <p className="label-eyebrow text-lavender">First Entrance</p>
              <p className="mt-2 text-bone/80 text-sm leading-relaxed">
                A temporary password was set for you. Please choose a new one to complete your access to the Atelier.
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <KeyRound className="text-lavender" size={20} />
          <p className="label-eyebrow text-bone/60">Rotate the Key</p>
        </div>

        <h1 className="mt-4 font-display text-bone text-4xl md:text-5xl leading-[1.05]">
          Set Your <em className="not-italic text-lavender">New Password</em>
        </h1>
        <p className="mt-4 font-display italic text-bone/70 text-lg leading-snug max-w-sm">
          Twelve characters minimum. Only the Curator holds the key.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="atelier-change-form">
          <div>
            <label className="label-eyebrow text-bone/60">Current Password</label>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="atelier-change-current"
              className="mt-2 w-full bg-transparent border-b border-bone/20 focus:border-lavender py-3 text-bone placeholder-bone/30 outline-none transition-colors"
              placeholder="••••••••••••"
            />
          </div>
          <div>
            <label className="label-eyebrow text-bone/60">New Password</label>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={12}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="atelier-change-new"
              className="mt-2 w-full bg-transparent border-b border-bone/20 focus:border-lavender py-3 text-bone placeholder-bone/30 outline-none transition-colors"
              placeholder="At least 12 characters"
            />
          </div>
          <div>
            <label className="label-eyebrow text-bone/60">Confirm New Password</label>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={12}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="atelier-change-confirm"
              className="mt-2 w-full bg-transparent border-b border-bone/20 focus:border-lavender py-3 text-bone placeholder-bone/30 outline-none transition-colors"
              placeholder="Repeat your new password"
            />
          </div>

          {error && (
            <p data-testid="atelier-change-error" className="text-sm text-lavender">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="atelier-change-submit"
            className="w-full inline-flex items-center justify-center px-8 py-4 bg-lavender text-obsidian text-[12px] uppercase tracking-[0.3em] hover:bg-bone transition-colors disabled:opacity-60"
          >
            {submitting ? "Sealing…" : "Set New Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
